import { Voucher } from '../../../../../../../api/vouchers/types';
import { documentTypesEnum, voucherStatusesEnum } from '../../../../../../../api/vouchers/helpers';
import { getToFixedNumber } from '../../../../../../../utils/mathUtils';

/**
 * How much has already been paid back, from a travel file's documents.
 *
 * ONE repay is TWO documents: TravelBooster issues a Payment Requisition,
 * finance confirms it, and the PRQ converts to a remittance. Summing both
 * halves counts every completed repay twice — 15,162 repays in galor are
 * exactly one PRQ plus one remittance of identical amount.
 *
 * `repayForDocumentNumber` is what ties the two together (both point at the
 * receipt being repaid). Group by it and take the greater side per group: a
 * converted repay counts once, a part-converted one counts its full PRQ total,
 * and a repay issued straight as a remittance still counts. Note `isCovering`
 * does NOT record this — not one of the 103,493 repay documents carries it.
 *
 * A document with no link counts on its own. Galor's "standalone" repays —
 * flagged only by a "Refund to…" text marker — do NOT pair up: of 25,787 such
 * remittances, ZERO have a matching standalone PRQ on the same account for the
 * same amount. They are one repay, one document, so summing them is right.
 */
const repaidGroups = (vouchers: Voucher[] | null | undefined) => {
    const live = (vouchers || [])
        .filter((v) => v.status !== voucherStatusesEnum.CANCEL)
        .filter((v) => !!(v.info as any)?.isRepay);

    const groups = new Map<string, { prq: number; rmt: number }>();
    let unlinkedTotal = 0;
    live.forEach((v) => {
        // The documents query returns this as a top-level column, not inside
        // `info` — json_build_object is at Postgres' 100-argument ceiling.
        // Read both so either shape works.
        const link = (v as any).repayForDocumentNumber ?? (v.info as any)?.repayForDocumentNumber;
        const amount = Number(v.info?.amount) || 0;
        if (link == null) {
            unlinkedTotal += amount;
            return;
        }
        const bucket = groups.get(String(link)) || { prq: 0, rmt: 0 };
        if (v.type === documentTypesEnum.REMITTANCE) bucket.rmt += amount;
        else bucket.prq += amount;
        groups.set(String(link), bucket);
    });
    return { groups, unlinkedTotal };
};

/** Everything already paid back on the file (Issue Repay's ceiling). */
export const totalAlreadyRepaid = (vouchers: Voucher[] | null | undefined): number => {
    const { groups, unlinkedTotal } = repaidGroups(vouchers);
    let total = unlinkedTotal;
    groups.forEach(({ prq, rmt }) => {
        total += Math.max(prq, rmt);
    });
    return getToFixedNumber(total);
};

/** A document's currency id, whether stored as an id or as a currency object. */
export const currencyIdOf = (value: any): string | null => {
    const currency = value && typeof value === 'object' && 'info' in value ? value.info?.currency : value;
    const id = currency && typeof currency === 'object' ? currency._id : currency;
    return id == null || id === '' ? null : String(id);
};

/**
 * What has already been paid back against ONE receipt, by its number.
 *
 * Amounts are only added up in one currency. When `receiptCurrency` is given and
 * a live repay of this receipt was issued in a different currency, the answer is
 * null — "not known" — rather than a sum of shekels and dollars.
 */
export const repaidAgainstReceipt = (
    vouchers: Voucher[] | null | undefined,
    receiptNumber: string | number | null | undefined,
    receiptCurrency?: unknown
): number | null => {
    if (receiptNumber == null || receiptNumber === '') return 0;
    const wanted = receiptCurrency == null ? null : currencyIdOf(receiptCurrency);
    if (wanted) {
        const mixed = (vouchers || []).some((v) => {
            if (v.status === voucherStatusesEnum.CANCEL || !(v.info as any)?.isRepay) return false;
            const link = (v as any).repayForDocumentNumber ?? (v.info as any)?.repayForDocumentNumber;
            if (link == null || String(link) !== String(receiptNumber)) return false;
            const own = currencyIdOf(v);
            return own != null && own !== wanted;
        });
        if (mixed) return null;
    }
    const bucket = repaidGroups(vouchers).groups.get(String(receiptNumber));
    return bucket ? getToFixedNumber(Math.max(bucket.prq, bucket.rmt)) : 0;
};
